import { useEffect, useRef } from 'react';
import { ModelRow, RegionEntry, regionLabel } from '../types';
import { geographyLabel, geographyOf } from '../lib/regions';
import { isRegionActive, lifecycleStatus, routesForModel, tierLabel } from '../lib/models';
import { CopyButton } from './CopyButton';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface Props {
  row: ModelRow;
  regionLabels: Record<string, string>;
  onClose: () => void;
}

function RegionChip({ region, regionLabels }: { region: string; regionLabels: Record<string, string> }) {
  return <span className="badge badge-region-xs" title={regionLabel(region, regionLabels)}>{region}</span>;
}

function statusBadge(regions: ModelRow['regions']) {
  const status = lifecycleStatus(regions);
  if (status === 'ACTIVE') return <span className="badge badge-active">Active</span>;
  if (status === 'LEGACY') return <span className="badge badge-legacy">Legacy</span>;
  return <span className="badge badge-mixed" title="Active in some regions, Legacy/deprecated in others — check the region you're calling">Mixed status</span>;
}

export function ModelDrawer({ row, regionLabels, onClose }: Props) {
  const drawerRef = useRef<HTMLElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Move focus into the drawer on open, and hand it back to whatever
    // triggered the drawer (a table row, model-card, or entry-card link)
    // on close — otherwise keyboard focus is silently dropped to <body>.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeBtnRef.current?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !drawerRef.current) return;
      // Trap focus inside the drawer while it's open — without this, Tab
      // walks focus into the (visually hidden but still present) page behind it.
      const focusables = drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      previouslyFocused?.focus();
    };
  }, [onClose]);

  const nativeRegionEntries = row.regions.filter(re => !re.isCri);
  const byGeo = new Map<string, RegionEntry[]>();
  for (const re of nativeRegionEntries) {
    const geo = geographyOf(re.region) ?? 'other';
    if (!byGeo.has(geo)) byGeo.set(geo, []);
    byGeo.get(geo)!.push(re);
  }
  const routing = routesForModel(row);
  const hasSourceRoutes = row.inferenceProfiles.some(p => p.sourceRoutes && Object.keys(p.sourceRoutes).length > 0);

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <aside
        ref={drawerRef}
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`${row.modelName} availability details`}
        onClick={e => e.stopPropagation()}
      >
        <div className="drawer-header">
          <div>
            <div className="drawer-provider">{row.providerName}</div>
            <h2 className="drawer-title">{row.modelName}</h2>
          </div>
          <button ref={closeBtnRef} className="drawer-close" onClick={onClose} aria-label="Close details">✕</button>
        </div>

        <div className="drawer-badges">
          {statusBadge(row.regions)}
          {row.streamingSupported && <span className="badge badge-stream">stream</span>}
          {row.inputModalities.map(m => <span key={`i-${m}`} className="badge badge-modality">in: {m.toLowerCase()}</span>)}
          {row.outputModalities.map(m => <span key={`o-${m}`} className="badge badge-modality">out: {m.toLowerCase()}</span>)}
        </div>

        <div className="drawer-model-id">
          <code className="model-id">{row.modelId}</code>
          <CopyButton text={row.modelId} />
        </div>

        <section className="drawer-section">
          <h3>In-region availability</h3>
          <p className="drawer-hint">
            Requests are processed in the region you call — data stays there.
            {' '}A region marked <span className="badge badge-region badge-region-legacy" style={{ pointerEvents: 'none' }}>⚠ legacy</span> is
            deprecated there specifically, even where the model shows Active overall.
          </p>
          {nativeRegionEntries.length === 0 ? (
            <p className="muted-dash">None — this model is only reachable via cross-region routing.</p>
          ) : (
            [...byGeo.entries()].map(([geo, regions]) => (
              <div key={geo} className="drawer-geo-group">
                <span className="drawer-geo-label">{geo === 'other' ? 'Other' : geographyLabel(geo)}</span>
                <div className="drawer-chips">
                  {regions.map(re => {
                    const tier = tierLabel(re.inferenceTypesSupported);
                    return (
                      <span
                        key={re.region}
                        className={`badge badge-region${isRegionActive(re) ? '' : ' badge-region-legacy'}`}
                        title={`${tier ? `${tier} · ` : ''}${isRegionActive(re) ? 'in-region' : 'legacy/deprecated in this region'}`}
                      >
                        {re.region}
                        <span className="badge-city">
                          {regionLabels[re.region] ?? ''}{tier ? ` · ${tier}` : ''}
                        </span>
                      </span>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </section>

        <section className="drawer-section">
          <h3>Cross-region routing</h3>
          <p className="drawer-hint">
            Where requests can be processed depends on the region you call from.
            Destinations can include regions your account has not opted into.
          </p>
          {routing.length === 0 ? (
            <p className="muted-dash">No geographic routing profiles for this model.</p>
          ) : (
            routing.map(({ profile, groups }) => (
              <div key={profile.profileId} className="drawer-profile">
                <div className="profile-header">
                  <span className={`badge badge-zone-${profile.prefix}`}>{profile.prefix}</span>
                  <code className="profile-id">{profile.profileId}</code>
                  <CopyButton text={profile.profileId} />
                </div>
                {groups.map(g => (
                  <div key={g.sources.join(',')} className="drawer-route">
                    <div className="drawer-route-col">
                      <span className="drawer-route-label">Calling from</span>
                      <div className="drawer-chips">
                        {g.sources.map(r => <RegionChip key={r} region={r} regionLabels={regionLabels} />)}
                      </div>
                    </div>
                    <span className="drawer-route-arrow" aria-label="routes to">→</span>
                    <div className="drawer-route-col">
                      <span className="drawer-route-label">May be processed in</span>
                      <div className="drawer-chips">
                        {g.destinations.map(r => <RegionChip key={r} region={r} regionLabels={regionLabels} />)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
          {routing.length > 0 && !hasSourceRoutes && (
            <p className="drawer-hint">
              Per-source routing has not been collected yet for this dataset —
              showing the profile&rsquo;s member regions as both sources and destinations.
            </p>
          )}
        </section>
      </aside>
    </div>
  );
}
